import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import SEO from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import {
  Phone, Mail, MapPin, Clock, Building2, Users, MessageCircle,
  HelpCircle, CheckCircle, ArrowRight, Calendar, Globe,
  Briefcase, Target, Shield, Zap
} from "lucide-react";
import { AuroraHero } from "@/components/ui/aurora-hero";

const Contact = () => {
  const offices = [
    {
      city: "São Paulo",
      region: "Sudeste", 
      address: "Av. Paulista, 1000 - Bela Vista",
      cep: "01310-100",
      phone: "+55 (11) 3456-7890",
      email: "saopaulo@futurestation.com.br",
      manager: "Carlos Silva",
      specialties: ["Desenvolvimento", "Consultoria", "Suporte"],
      status: "Sede Principal"
    },
    {
      city: "Rio de Janeiro",
      region: "Sudeste",
      address: "Av. Rio Branco, 500 - Centro", 
      cep: "20040-020",
      phone: "+55 (21) 3456-7890",
      email: "rio@futurestation.com.br",
      manager: "Ana Costa",
      specialties: ["Implementação", "Treinamento"],
      status: "Escritório Regional"
    },
    {
      city: "Belo Horizonte",
      region: "Sudeste",
      address: "Av. Afonso Pena, 800 - Centro",
      cep: "30130-002", 
      phone: "+55 (31) 3456-7890",
      email: "bh@futurestation.com.br",
      manager: "Roberto Santos",
      specialties: ["Consultoria", "Suporte"],
      status: "Escritório Regional"
    },
    {
      city: "Brasília",
      region: "Centro-Oeste",
      address: "SCS Quadra 02 - Asa Sul",
      cep: "70300-500",
      phone: "+55 (61) 3456-7890", 
      email: "brasilia@futurestation.com.br",
      manager: "Márcia Oliveira",
      specialties: ["Setor Público", "Compliance"],
      status: "Escritório Estratégico"
    }
  ];

  const contactMethods = [
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "WhatsApp",
      info: "+55 (81) 98165-8439",
      description: "Atendimento rápido e direto",
      action: "Enviar Mensagem",
      link: "https://wa.me/5581981658439?text=Olá! Gostaria de conhecer as soluções Future Station."
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Telefone Principal",
      info: "+55 (11) 3456-7890",
      description: "Horário comercial: 8h às 18h",
      action: "Ligar Agora",
      link: "tel:+551134567890"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "E-mail Comercial",
      info: "contato@futurestation.com.br",
      description: "Resposta em até 24h",
      action: "Enviar E-mail",
      link: "mailto:contato@futurestation.com.br"
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Agendar Reunião",
      info: "Demonstração Online",
      description: "Conheça nossas soluções",
      action: "Agendar Demo",
      link: "https://wa.me/5581981658439?text=Olá! Gostaria de agendar uma demonstração das soluções Future Station."
    }
  ];

  const serviceTypes = [
    {
      icon: <Target className="w-5 h-5" />,
      name: "Consultoria Fluig",
      description: "Análise e otimização de processos"
    },
    {
      icon: <Briefcase className="w-5 h-5" />,
      name: "Implementação FluigApps",
      description: "Deploy de soluções pré-construídas"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      name: "Desenvolvimento Customizado",
      description: "Soluções específicas para seu negócio"
    },
    {
      icon: <Shield className="w-5 h-5" />,
      name: "Suporte e Manutenção",
      description: "Acompanhamento contínuo"
    }
  ];

  const faq = [
    {
      question: "Qual o prazo para implementação de um FluigApp?",
      answer: "Nossos FluigApps podem ser implementados em até 24 horas para a versão padrão. Customizações podem levar de 3 a 10 dias úteis."
    },
    {
      question: "Vocês oferecem suporte pós-implementação?",
      answer: "Sim, oferecemos planos de suporte com SLA de 4h para issues críticas e acompanhamento mensal da solução."
    },
    {
      question: "É possível integrar com outros sistemas além do TOTVS?",
      answer: "Sim, temos experiência em integrações com SAP, Oracle, sistemas legados e APIs REST/SOAP."
    },
    {
      question: "Qual o investimento médio para um projeto Fluig?",
      answer: "O investimento varia de R$ 15.000 a R$ 150.000 dependendo da complexidade. Oferecemos orçamento sem compromisso."
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Entre em Contato - Future Station"
        description="Fale com nossos especialistas em Fluig e TOTVS. Escritórios em 4 capitais, atendimento WhatsApp 24h e orçamento sem compromisso. Transforme seus processos hoje mesmo!"
        keywords={['contato future station', 'orçamento fluig', 'consulta gratuita', 'especialistas totvs', 'whatsapp fluig', 'escritórios brasil', 'proposta comercial', 'demonstração fluig']}
        type="website"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section id="hero">
          <AuroraHero>
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold mb-6 fs-gradient-text">
                Entre em Contato
              </h1>
              <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto mb-8">
                Presente em 4 capitais brasileiras, estamos prontos para transformar
                seus processos empresariais. Fale conosco e descubra como mais de
                50.000 horas de experiência podem revolucionar sua empresa.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center w-full sm:w-auto">
                <Button
                  variant="fs-primary"
                  size="lg"
                  className="w-full sm:w-auto"
                  onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Gostaria de conhecer as soluções Future Station.', '_blank')}
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp Direto
                </Button>
                <Button
                  variant="fs-secondary"
                  size="lg"
                  className="w-full sm:w-auto"
                  onClick={() => document.getElementById('form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Formulário Completo
                </Button>
              </div>
            </div>
          </AuroraHero>
        </section>

        {/* Métodos de Contato */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Como Falar Conosco
              </h2>
              <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Múltiplos canais de atendimento para sua conveniência
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactMethods.map((method, index) => (
                <Card key={index} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:border-fs-purple-500/40 transition-all duration-300 text-center group">
                  <CardContent className="p-6">
                    <div className="text-fs-purple-500 mb-4 flex justify-center group-hover:text-fs-cyan-500 transition-colors">
                      {method.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-foreground dark:text-white mb-2">
                      {method.title}
                    </h3>
                    <p className="text-fs-purple-500 font-medium mb-2">
                      {method.info}
                    </p>
                    <p className="text-muted-foreground dark:text-white/60 text-sm mb-4">
                      {method.description}
                    </p>
                    <Button 
                      variant="fs-secondary" 
                      size="sm" 
                      className="w-full"
                      onClick={() => window.open(method.link, '_blank')}
                    >
                      {method.action}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Container>

        {/* Nossos Escritórios */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Nossos Escritórios
              </h2>
              <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Presença nacional com atendimento especializado em 4 capitais brasileiras
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {offices.map((office, index) => (
                <Card key={index} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:border-fs-purple-500/40 transition-all duration-300 h-full">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-6 h-6 text-fs-purple-500" />
                        <CardTitle className="text-foreground dark:text-white text-xl">{office.city}</CardTitle>
                      </div>
                      <span className="text-xs px-2 py-1 bg-fs-purple-500/20 text-fs-purple-500 rounded">
                        {office.status}
                      </span>
                    </div>
                    <CardDescription className="text-muted-foreground dark:text-white/70">
                      {office.region} • {office.address}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">CEP</p>
                        <p className="text-foreground dark:text-white">{office.cep}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Gerente</p>
                        <p className="text-foreground dark:text-white">{office.manager}</p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-muted-foreground text-sm mb-2">Especialidades</p>
                      <div className="flex flex-wrap gap-2">
                        {office.specialties.map((specialty, specIndex) => (
                          <span key={specIndex} className="text-xs px-2 py-1 bg-muted text-muted-foreground rounded">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col space-y-2 pt-4">
                      <Button 
                        variant="fs-secondary" 
                        size="sm"
                        onClick={() => window.open(`tel:${office.phone.replace(/\s/g, '')}`, '_blank')}
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        {office.phone}
                      </Button>
                      <Button 
                        variant="fs-secondary" 
                        size="sm"
                        onClick={() => window.open(`mailto:${office.email}`, '_blank')}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        {office.email}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Container>

        {/* Formulário Corporativo */}
        <Container className="py-20" id="form">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Formulário Corporativo
              </h2>
              <p className="text-lg text-muted-foreground dark:text-white/70 max-w-2xl mx-auto">
                Preencha com detalhes sobre seu projeto para um atendimento personalizado
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-12">
              {/* Formulário Principal */}
              <div className="lg:col-span-2">
                <Card className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none">
                  <CardHeader>
                    <CardTitle className="text-2xl fs-gradient-text">
                      Solicitar Proposta Personalizada
                    </CardTitle>
                    <CardDescription className="text-muted-foreground dark:text-white/70">
                      Quanto mais detalhes você fornecer, melhor poderemos ajudá-lo
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Informações Pessoais */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                          Nome Completo *
                        </label>
                        <Input
                          placeholder="Seu nome completo"
                          className="bg-background dark:bg-gray-900/80 border-border text-foreground dark:text-white text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                          E-mail Corporativo *
                        </label>
                        <Input
                          type="email"
                          placeholder="nome@empresa.com.br"
                          className="bg-background dark:bg-gray-900/80 border-border text-foreground dark:text-white text-base"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                          Telefone/WhatsApp *
                        </label>
                        <Input
                          placeholder="(11) 99999-9999"
                          className="bg-background dark:bg-gray-900/80 border-border text-foreground dark:text-white text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                          Cargo
                        </label>
                        <Input
                          placeholder="Diretor de TI, Gerente de Processos..."
                          className="bg-background dark:bg-gray-900/80 border-border text-foreground dark:text-white text-base"
                        />
                      </div>
                    </div>

                    {/* Informações da Empresa */}
                    <div className="border-t border-border pt-6">
                      <h3 className="text-lg font-semibold text-foreground dark:text-white mb-4">Informações da Empresa</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                            Nome da Empresa *
                          </label>
                          <Input
                            placeholder="Razão social da empresa"
                            className="bg-background dark:bg-gray-900/80 border-border text-foreground dark:text-white text-base"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                            Segmento
                          </label>
                          <Select>
                            <SelectTrigger className="bg-background dark:bg-gray-900/80 border-fs-purple-500/30 text-foreground dark:text-white">
                              <SelectValue placeholder="Selecione o segmento" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="industria">Indústria</SelectItem>
                              <SelectItem value="comercio">Comércio</SelectItem>
                              <SelectItem value="servicos">Serviços</SelectItem>
                              <SelectItem value="publico">Setor Público</SelectItem>
                              <SelectItem value="financeiro">Financeiro</SelectItem>
                              <SelectItem value="saude">Saúde</SelectItem>
                              <SelectItem value="educacao">Educação</SelectItem>
                              <SelectItem value="outros">Outros</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                            Número de Funcionários
                          </label>
                          <Select>
                            <SelectTrigger className="bg-background dark:bg-gray-900/80 border-fs-purple-500/30 text-foreground dark:text-white">
                              <SelectValue placeholder="Porte da empresa" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="micro">Até 10 funcionários</SelectItem>
                              <SelectItem value="pequena">11 a 50 funcionários</SelectItem>
                              <SelectItem value="media">51 a 250 funcionários</SelectItem>
                              <SelectItem value="grande">Mais de 250 funcionários</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                            Localização Principal
                          </label>
                          <Input
                            placeholder="Cidade, Estado"
                            className="bg-background dark:bg-gray-900/80 border-fs-purple-500/30 text-foreground dark:text-white text-base"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Projeto e Necessidades */}
                    <div className="border-t border-fs-purple-500/20 pt-6">
                      <h3 className="text-lg font-semibold text-foreground dark:text-white mb-4">Detalhes do Projeto</h3>
                      <div>
                        <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                          Tipo de Serviço Desejado *
                        </label>
                        <Select>
                          <SelectTrigger className="bg-background dark:bg-gray-900/80 border-fs-purple-500/30 text-foreground dark:text-white">
                            <SelectValue placeholder="Selecione o tipo de serviço" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="consultoria">Consultoria Fluig</SelectItem>
                            <SelectItem value="fluigapp">Implementação FluigApp</SelectItem>
                            <SelectItem value="desenvolvimento">Desenvolvimento Customizado</SelectItem>
                            <SelectItem value="suporte">Suporte e Manutenção</SelectItem>
                            <SelectItem value="integracao">Integração TOTVS</SelectItem>
                            <SelectItem value="migracao">Migração de Sistema</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                          Usa TOTVS atualmente?
                        </label>
                        <Select>
                          <SelectTrigger className="bg-background dark:bg-gray-900/80 border-fs-purple-500/30 text-foreground dark:text-white">
                            <SelectValue placeholder="Selecione uma opção" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="protheus">Sim, TOTVS Protheus</SelectItem>
                            <SelectItem value="rm">Sim, TOTVS RM</SelectItem>
                            <SelectItem value="datasul">Sim, TOTVS Datasul</SelectItem>
                            <SelectItem value="fluig">Sim, TOTVS Fluig</SelectItem>
                            <SelectItem value="outros-totvs">Sim, outros produtos TOTVS</SelectItem>
                            <SelectItem value="nao">Não usa TOTVS</SelectItem>
                            <SelectItem value="avaliando">Avaliando TOTVS</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                          Prazo Desejado
                        </label>
                        <Select>
                          <SelectTrigger className="bg-background dark:bg-gray-900/80 border-fs-purple-500/30 text-foreground dark:text-white">
                            <SelectValue placeholder="Quando gostaria de implementar?" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="imediato">Imediato (até 30 dias)</SelectItem>
                            <SelectItem value="curto">Curto prazo (1-3 meses)</SelectItem>
                            <SelectItem value="medio">Médio prazo (3-6 meses)</SelectItem>
                            <SelectItem value="longo">Longo prazo (+ 6 meses)</SelectItem>
                            <SelectItem value="avaliando">Ainda avaliando</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-muted-foreground dark:text-white/70 mb-2">
                          Descrição do Projeto
                        </label>
                        <Textarea
                          placeholder="Conte-nos mais sobre seu projeto, processos que precisam ser automatizados, desafios atuais, expectativas..."
                          rows={6}
                          className="bg-background dark:bg-gray-900/80 border-fs-purple-500/30 text-foreground dark:text-white resize-none text-base"
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input type="checkbox" id="terms" className="rounded" />
                      <label htmlFor="terms" className="text-sm text-muted-foreground dark:text-white/70">
                        Concordo em receber contato da Future Station sobre este projeto
                      </label>
                    </div>
                    
                    <Button variant="fs-primary" size="lg" className="w-full">
                      <ArrowRight className="w-5 h-5 mr-2" />
                      Enviar Solicitação
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Tipos de Serviço */}
                <Card className="bg-card dark:bg-gray-900/50 border-fs-purple-500/20">
                  <CardHeader>
                    <CardTitle className="text-foreground dark:text-white flex items-center">
                      <Briefcase className="w-5 h-5 mr-2 text-fs-purple-500" />
                      Nossos Serviços
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {serviceTypes.map((service, index) => (
                        <div key={index} className="flex items-start space-x-3 p-3 rounded hover:bg-fs-purple-500/10 transition-colors">
                          <div className="text-fs-purple-500 mt-1">
                            {service.icon}
                          </div>
                          <div>
                            <h4 className="text-foreground dark:text-white font-medium text-sm">{service.name}</h4>
                            <p className="text-muted-foreground dark:text-white/60 text-xs">{service.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Garantias */}
                <Card className="bg-gradient-to-br from-fs-purple-500/10 to-fs-cyan-500/10 border-fs-purple-500/20">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-4 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2 text-fs-purple-500" />
                      Nossas Garantias
                    </h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-fs-purple-500 flex-shrink-0" />
                        <span className="text-muted-foreground dark:text-white/70">Resposta em até 24h</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-fs-purple-500 flex-shrink-0" />
                        <span className="text-muted-foreground dark:text-white/70">Orçamento sem compromisso</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-fs-purple-500 flex-shrink-0" />
                        <span className="text-muted-foreground dark:text-white/70">Demonstração gratuita</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-fs-purple-500 flex-shrink-0" />
                        <span className="text-muted-foreground dark:text-white/70">Suporte especializado</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Horários */}
                <Card className="bg-card dark:bg-gray-900/50 border-fs-purple-500/20">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-foreground dark:text-white mb-4 flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-fs-purple-500" />
                      Horários de Atendimento
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground dark:text-white/70">Segunda a Sexta:</span>
                        <span className="text-foreground dark:text-white">8h às 18h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground dark:text-white/70">Sábado:</span>
                        <span className="text-foreground dark:text-white">8h às 12h</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground dark:text-white/70">WhatsApp:</span>
                        <span className="text-foreground dark:text-white">24h (emergências)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground dark:text-white/70">Fuso Horário:</span>
                        <span className="text-foreground dark:text-white">Brasília (UTC-3)</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </Container>

        {/* FAQ */}
        <Container className="py-20">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 fs-gradient-text">
                Perguntas Frequentes
              </h2>
              <p className="text-lg text-muted-foreground dark:text-white/70">
                Respostas para as principais dúvidas sobre nossos serviços
              </p>
            </div>

            <div className="space-y-6">
              {faq.map((item, index) => (
                <Card key={index} className="bg-card dark:bg-gray-900/50 border-fs-purple-500/20 hover:border-fs-purple-500/40 transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <HelpCircle className="w-6 h-6 text-fs-purple-500 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-semibold text-foreground dark:text-white mb-3">
                          {item.question}
                        </h3>
                        <p className="text-muted-foreground dark:text-white/70 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-muted-foreground dark:text-white/70 mb-4">Não encontrou sua resposta?</p>
              <Button
                variant="fs-primary"
                onClick={() => window.open('https://wa.me/5581981658439?text=Olá! Tenho uma dúvida que não está no FAQ do site.', '_blank')}
              >
                Falar com Especialista
              </Button>
            </div>
          </div>
        </Container>
      </main>
      
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Contact;